import { useAuthFetch } from "@p0security/frontend-core";
import { ErrorDisplay, FirestoreDoc } from "@p0security/frontend-core/react";
import { Finding } from "@p0security/shared/types/assessment/finding";
import { Button, Flex, Modal, Spin, message } from "@p0security/ui";
import { JiraIcon } from "components/Integrations/Jira/Jira";
import pluralize from "pluralize";
import { useCallback, useMemo, useState } from "react";

import { MonitorActionProps } from "../pages/MonitorResults";
import { useFindingsStateUpdates } from "./finding/useFindingsStateUpdates";

/** Bulk create issues */
export const FindingsAssignModal: React.FC<MonitorActionProps> = ({
  actOn,
  state,
}) => {
  const [staged, setStaged] = useState<FirestoreDoc<Finding>[]>([]);
  const [error, setError] = useState<string>();
  const authFetch = useAuthFetch(setError);

  const { assignFindings } = useFindingsStateUpdates(authFetch);

  const assignable = useMemo(() => actOn.filter((f) => !f.data.issue), [actOn]);

  const openAssignModal = useCallback(
    () => setStaged(assignable),
    [assignable]
  );
  const closeAssignModal = useCallback(() => setStaged([]), []);

  const [inProgress, setInProgress] = useState<boolean>(false);

  const submitAssign = useCallback(async () => {
    setError(undefined);
    setInProgress(true);
    const response = await assignFindings(staged);
    setInProgress(false);
    if (response?.ok) {
      // otherwise 'error' is set
      message.success(
        `Successfully assigned ${staged.length} ${pluralize(
          "findings",
          staged.length
        )}`
      );
      setStaged([]);
    }
  }, [assignFindings, staged]);

  return (
    <>
      <Button
        onClick={openAssignModal}
        disabled={assignable.length == 0}
        // block display for proper icon alignment
        className="block"
      >
        {JiraIcon}
        &nbsp;Assign
      </Button>
      <Modal
        footer={false}
        maskClosable={false}
        closable={!inProgress}
        open={staged.length > 0}
        onCancel={closeAssignModal}
        /* eslint-disable-line react/forbid-component-props */ style={
          state === "open" ? { minWidth: "600px" } : {}
        }
      >
        {error && (
          <ErrorDisplay title="Could not assign findings" error={error} />
        )}
        <Flex gap="0.7em" vertical>
          {inProgress ? (
            <div>
              <Spin /> &nbsp; Assigning
            </div>
          ) : (
            <>
              <div>
                Assign {staged.length} {pluralize("finding", staged.length)}?
              </div>
              <div>
                <Button onClick={submitAssign}>OK</Button>
              </div>
            </>
          )}
        </Flex>
      </Modal>
    </>
  );
};
